import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as auth0 from 'auth0-js';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class SsoAuthService {

  private tokenRenewalTimeout: any;
  private currentUser: any = null;
  private onEventCallbacks = [];
  private namespace = 'http://default/';

  private auth0: auth0.WebAuth;

  constructor(public router: Router) {
  }

  /**
   * @name configure
   * @description Configures the authService.
   * @param {any} options
   * namespace    The namespace used for custom claims
   * clientID     Auth0 ClientID
   * domain       Auth0 Domain
   * callbackUrl  URL to return to after login
   */
  public configure(options: any) {
    options = options || {};
    this.namespace = options.namespace;
    this.auth0 = new auth0.WebAuth({
      clientID: options.clientID,
      domain: options.domain,
      responseType: 'token id_token',
      redirectUri: options.callbackUrl,
      audience: options.audience,
      scope: 'openid profile email email_verified name'
    });
  }

  /**
   * @name signUp
   * @description Initates the Universal Login with the inital screen set to Sign Up
   * @param options Various options that override the default auth0 options for logging in such as redirect_url
   */
  public signUp(options: any): void {
    options = options || {};
    options.initialScreen = 'signUp';
    this.auth0.authorize(options);
  }

  /**
  * @name signUp
  * @description Initates the Universal Login with the inital screen set to Login
  * @param options Various options that override the default auth0 options for logging in such as redirect_url
  */
  public login(options: any): void {
    options = options || {};
    options.initialScreen = 'login';
    this.auth0.authorize(options);
  }

  /**
   * @name getProfileErrors
   * @description Private function that will validate the users profile for business rules. The email must be present and verified.
   *              Otherwise an error object is returned with the code and user_id. If valid the function will return null
   * @param {any} profile The users profile
   */
  private getProfileErrors(profile: any): any {
    if (!profile.email) {
      return {
        code: 'email',
        user_id: profile.sub,
        isError: true
      };
   }
   if (!profile.email_verified) {
      return {
        code: 'email_verified',
        user_id: profile.sub,
        isError: true
      };
   }
   return null;
}


  /**
   * @name setCurrentUser
   * @description Private function, sets the profile in both a local variable and local storage (for when page is refreshed).
   *              If set to null it will clear it from local storage
   * @param {any} profile The user profile
   */
  private setCurrentUser(profile) {
    this.currentUser = profile;
    if (profile) {
      localStorage.setItem('current_user', JSON.stringify(profile));
    } else {
      localStorage.removeItem('current_user');
    }
  }

  /**
   * @name getCurrentUser
   * @description Returns the current user from the local variable if exists or otherwise it attempts to retrieve it from local storage
   * */
  public getCurrentUser(): any {
    if (!this.currentUser) {
        const storedProfile = localStorage.getItem('current_user');
        if (storedProfile) {
          this.currentUser = JSON.parse(storedProfile);
        }
    }
    return this.currentUser;
  }

  /**
   * @name normalizeClaims
   * @description Private function that copies the namespaced claims properties with properties with the namespce removed.
   *              Namespace is configured by the provider
   * */
  private normalizeClaims(profile) {
    // conver the namespaced claims into simple properties
    for (const property in profile) {
        if (profile.hasOwnProperty(property)) {
            if (property.startsWith(this.namespace)) {
                profile[property.replace(this.namespace, '')] = profile[property];
            }
        }
    }
  }


  /**
  * @name handleAuthentication
  * @description Handles the callback from the OAuth server processing the url and extraing the auth response. If the token is valid
  *              the token information will be stored in local storage and the users profile will be retrieved and returned.
  *              Callback functions can be registered using {@link registerEventCallback} and these will be executed on
  *              successfull authentication passing in the profile.
  * @returns A promise that returns the users profile if successfull
  */
  public handleAuthentication() {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash((err, authResult) => {
        // check the auth result is valid
        if (authResult && authResult.accessToken && authResult.idToken) {

            const profile = authResult.idTokenPayload;
            this.normalizeClaims(profile);
            // validate the profile for business rules
            const errors = this.getProfileErrors(profile);
            if (!errors) {
              this.setSession(authResult);
              this.setCurrentUser(profile);
              this.fireEventCallbacks('authenticated', profile);
              resolve(profile);
            } else {
              reject(errors);
            }
        } else if (err) {
            console.error(err);
            reject({
                code: err.error,
                description: err.errorDescription || err.error_description,
                isError: true
            });
        } else {
            console.error('Access token was not returned');
            reject({
                code: 'noaccesstoken',
                description: 'a valid access token was not returned',
                isError: true
            });
        }
    });
    });
  }

  /**
  * @name checkSSO
  * @description Checks to see if the user is authenticated via single sign on (logged in via a related application).
  *              If so their session will be created and they will be authenticated with the application. Callback functions
  *              can be registered using {@link registerEventCallback} and these will be executed on successfull
  *              authentication passing in the profile.
  * @returns     A promise that returns the users profile if successfull.
  */
  public checkSSO() {
    console.log('Checking SSO...');
    return new Promise((resolve, reject) => {
      this.auth0.checkSession({},
        (err, result) => {
            if (err) {
                // todo: what to do here
                console.error('Unable to retrieve session. ' + (err.errorDescription || err.error_description));
                reject({
                    code: err.error,
                    description: err.errorDescription || err.error_description,
                    isError: true
                });
            } else {

                const profile = result.idTokenPayload;
                this.normalizeClaims(profile);
                // validate the profile for business rules
                const errors = this.getProfileErrors(profile);
                if (!errors) {
                  this.setSession(result);
                  this.setCurrentUser(profile);
                  this.fireEventCallbacks('authenticated', profile);
                  resolve(profile);
                } else {
                  reject(errors);
                }
            }
        }
    );

    });
  }

  /**
   * @name setSession
   * @description Private function to set the auth token data in local storage and calcualte the expiry date time.
   *              It schedules the token renewal to retrieve a new token before it expires
   * @param {any} authResult
   */
  private setSession(authResult) {
    // Set the time that the Access Token will expire at
    const expiry = (authResult.expiresIn * 1000) + new Date().getTime();
    const expiresAt = JSON.stringify(expiry);
    localStorage.setItem('access_token', authResult.accessToken);
    localStorage.setItem('id_token', authResult.idToken);
    localStorage.setItem('expires_at', expiresAt);

    console.log('Access token expire at ' + new Date(expiry));

    // schedule a token renewal
    this.scheduleRenewal();
  }

  /**
   * @name getToken
   * @description Retreives the current access token
   * @returns The current access token
   */
  public getToken() {
    return localStorage.getItem('access_token');
  }

  /**
   * @name logout
   * @description Removes the current access token which logs the user out from the application and calls logout on the central OAuth server
   *              to logout the user centrally.
   * @param {any} returnTo The url to return to after logging out. Note this must be registerd with the application in the OAuth server
   */
  public logout(returnTo) {
    // Remove tokens and expiry time from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    this.setCurrentUser(null);
    clearTimeout(this.tokenRenewalTimeout);
    this.auth0.logout({
        returnTo: returnTo
    });
  }

  /**
   * @name logout
   * @description Removes the current access token which logs the user out from the application. Note that they will still be
   *             logged in to the central OAuth server
   * */
  public localLogout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    this.setCurrentUser(null);
    clearTimeout(this.tokenRenewalTimeout);
  }

  /**
   * @name isAuthenticated
   * @description Determines if the user is currently authenticated. They must have a valid non expired access token
   * @returns True if the user is currently authenticated otherwise false
   */
  public isAuthenticated() {
    // Check whether the current time is past the
    // Access Token's expiry time
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    return new Date().getTime() < expiresAt;
  }

  /**
  * @name registerEventCallback
  * @description Registers a callback to be executed on various events. Events are:
  *              'authenticated' - fired on a user authenticating. Either via an explict logon or implicitly via checking for single sign on
  *              'renewtokenfailed' - fired when background token refresh fails
  *              'usersessionchanged' - fired when user profile is different to signed in user
  * @param {any} callback Function to be executed when event raised
  * @param {string} event Name of event to get callback for: 'authenticated' | 'renewtokenfailed' | 'usersessionchanged'
  */
  public registerEventCallback(event: string, callback) {
    if (!this.onEventCallbacks[event]) {
      this.onEventCallbacks[event] = [];
    }
    this.onEventCallbacks[event].push(callback);
  }

  /**
  * @name refreshToken Triggers a renewel of the token, and will schedule automatic renewels again
  * @param {any} ignoreSessionChanges Will update the session even if its different from the current one
  */
  public refreshToken(ignoreSessionChanges) {
    clearTimeout(this.tokenRenewalTimeout);
    return this.renewToken(ignoreSessionChanges);
  }

  /**
  * @name renewToken
  * @description Private function called to renew the token. This is scheduled to be called before the token exprires
  * */
  private renewToken(ignoreSessionChanges) {
    console.log('Renewing token...');
    return new Promise((resolve, reject) => {
      let currentUser = this.getCurrentUser();
      this.auth0.checkSession({},
        (err, result) => {
            if (err) {

                console.error('Token unable to be renewed. ' + (err.errorDescription || err.error_description));
                // clear the logged in token
                this.localLogout();
                this.fireEventCallbacks('renewtokenfailed', null);
                reject({
                    code: err.error,
                    description: err.errorDescription || err.error_description,
                    isError: true
                });
            } else {

                const profile = result.idTokenPayload;

                //  user has changed
                if (!ignoreSessionChanges && currentUser.sub !== profile.sub) {
                  console.error('Users session has changed');
                  this.fireEventCallbacks('usersessionchanged', null);
                  reject({
                    code: 'sessionchanged',
                    description: 'The user session has changed',
                    isError: true
                  });
                } else {
                  this.normalizeClaims(profile);

                  // update the properties on the current user
                  currentUser = this.getCurrentUser();

                  _.assign(currentUser, profile);
                  this.setCurrentUser(currentUser);
                  // update the access token, which will scheudle another renew
                  this.setSession(result);
                  resolve();
                }
            }
        }
      );
    });
  }

  /**
  * @name scheduleRenewal
  * @description Private function that schedules a timeout to renew the token before it timesout.
  * */
  private scheduleRenewal() {
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    const delay = expiresAt - Date.now();

    if (delay > 0) {
        clearTimeout(this.tokenRenewalTimeout);
        // set renew for token expiry with a 10 second buffer
        this.tokenRenewalTimeout = setTimeout(() => { this.renewToken(false); }, delay - 10000);
    }
  }

  /**
  * @name hasAnyRole
  * @description Return true if the current user is authenticated and is a member of any of the provided roles
  * @param {any} roles The array (or comma seperated string list) of roles
  */
  public hasAnyRoles(roles) {
    if (!this.isAuthenticated() || !roles) {
        return false;
    }
    const user = this.getCurrentUser();

    if (typeof roles === 'string') {
        roles = roles.split(',');
    }

    return _.some(roles, (role) => this.userHasRole(user, role));
  }

  /**
  * @name userHasRole
  * @description Private function that return true if the user has the specified role
  * @param {any} user The user profile
  * @param {any} role The role to check for
  */
  private userHasRole(user, role) {
    return user.roles.indexOf(role) >= 0;
  }

  private fireEventCallbacks(event: string, data: any) {
    const callbacks = this.onEventCallbacks[event];
    if (callbacks) {
      _.each(callbacks, (callback) => { callback(data); });
    }
  }
}
